import React, { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const AuthorityBanner: React.FC = () => {
  const { elementRef, isVisible } = useScrollAnimation({ threshold: 0.3, triggerOnce: true });
  const [patientsCount, setPatientsCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (isVisible && !hasAnimated) {
      setHasAnimated(true);
      const duration = 2000;
      const targetValue = 5000;
      const startTime = Date.now();

      const animate = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        const currentValue = Math.floor(progress * targetValue);
        setPatientsCount(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [isVisible, hasAnimated]);

  const clinics = [
    {
      name: "Parahyba Mall",
      address: "R. Bacharel José de Oliveira Curchatuz, 850 - Jardim Oceania, João Pessoa - PB",
      mapLink: "https://maps.google.com/?q=R.+Bacharel+José+de+Oliveira+Curchatuz,+850,+João+Pessoa,+PB"
    },
    {
      name: "Shopping Tambiá",
      address: "R. Dep. Odon Bezerra, 184 - Tambiá, João Pessoa - PB",
      mapLink: "https://maps.google.com/?q=R.+Dep.+Odon+Bezerra,+184,+João+Pessoa,+PB"
    }
  ];

  return (
    <section ref={elementRef as React.RefObject<HTMLElement>} className="relative bg-white border-y border-gray-200/50 py-12 md:py-16">
      <div className="container-custom">
        {/* Stats Section */}
        <div className={`flex flex-wrap items-center justify-center gap-8 md:gap-16 mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          
          {/* Patients Counter */}
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary/10">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-gray-900">
                +{patientsCount.toLocaleString('pt-BR')}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Pacientes</div>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px h-12 bg-gray-200"></div>

          {/* Years */}
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary/10">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-gray-900">4 Anos</div>
              <div className="text-xs md:text-sm text-gray-600">De mercado</div>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px h-12 bg-gray-200"></div>

          {/* Units */}
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary/10">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-gray-900">2 Unidades</div>
              <div className="text-xs md:text-sm text-gray-600">Em João Pessoa</div>
            </div>
          </div>
        </div>

        {/* Divider Line */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-10"></div>

        {/* Clinics Section */}
        <div className={`flex flex-wrap items-center justify-center gap-6 md:gap-8 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {clinics.map((clinic, index) => (
            <div key={index} className="flex items-center gap-4 group">
              {/* Logo Circle */}
              <div className="w-12 h-12 rounded-full overflow-hidden shadow-md border-2 border-secondary/20">
                <img 
                  src="/assets/img/logo-01.PNG" 
                  alt={`Logo ${clinic.name}`}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Info */}
              <div className="flex flex-col">
                <span className="font-serif font-bold text-gray-900 text-sm md:text-base">
                  {clinic.name}
                </span>
                <span className="text-xs text-gray-600">{clinic.address}</span>
              </div>

              {/* Map Button */}
              <a
                href={clinic.mapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-secondary border border-secondary/30 rounded-full hover:bg-secondary hover:text-white transition-all duration-300 hover:scale-105"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Ver no mapa
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AuthorityBanner;
